import { Fragment, useRef, useState } from 'react';

type Board = Array<Array<string>>;
type Config = {
    id: string,
    b: 4 | 5,
    m: 'drag' | 'select'
};
type DragData = {
    row: number,
    cell: number,
    position: {
        x: number,
        y: number
    }
};
type Props = {
    config: Config,
    modal: boolean,
    setShowSettings?: React.Dispatch<React.SetStateAction<boolean>> | null,
    visible?: boolean | null,
}

const Settings = ({ config, modal, setShowSettings = null, visible = null }: Props): JSX.Element => {
    const [size, setSize] = useState<4 | 5>(config.b || 5);
    const [movement, setMovement] = useState<'drag' | 'select'>(config.m || 'drag');
    const [board, setBoard] = useState<Board>([['A', 'B'], ['C', 'D']]);
    const drag = useRef<HTMLDivElement | null>(null);
    const dragData = useRef<DragData | null>(null);
    const drags = useRef<number>(0);
    const firstSelection = useRef<HTMLDivElement | null>(null);
    const secondSelection = useRef<HTMLDivElement | null>(null);

    const confirmSettings = () => {
        const storage = {...config};

        if (! storage.id) {
            storage.id = '10000000-1000-4000-8000-100000000000'.replace(/[018]/g, c => (((+c ^ crypto.getRandomValues(new Uint8Array(1))[0]) & 15) >> +c / 4).toString(16));
        }

        storage.b = size;
        storage.m = movement;

        localStorage.setItem('config', btoa(JSON.stringify(storage)));

        const event = new CustomEvent('storage', {detail: {
            key: 'config',
            oldValue: config,
            newValue: storage
        }});

        window.dispatchEvent(event);

        if (setShowSettings) {
            setShowSettings(false);
        }
    };

    const swapSelectedCells = () => {
        if (! firstSelection.current || ! secondSelection.current) {
            return;
        }

        const boardElements = [...document.querySelectorAll('#settings .setting-cell-outer')];
        const firstIndex = boardElements.indexOf(firstSelection.current);
        const row = Math.floor(firstIndex / 2);
        const cell = firstIndex % 2;
        const secondIndex = boardElements.indexOf(secondSelection.current);
        const targetRow = Math.floor(secondIndex / 2);
        const targetCell = secondIndex % 2;
        const updatedBoard = [...board];

        [updatedBoard[row][cell], updatedBoard[targetRow][targetCell]] = [board[targetRow][targetCell], board[row][cell]];

        setBoard(updatedBoard);
    }

    const handleMouseDown = ({ nativeEvent: event }: React.MouseEvent<EventTarget> | React.TouchEvent<EventTarget>, row: number, cell: number): void => {
        if (! (event.target instanceof HTMLDivElement)) {
            return;
        }

        if (movement === 'drag') {
            const clientX = event instanceof MouseEvent ? event.clientX : event.touches[0].clientX;
            const clientY = event instanceof MouseEvent ? event.clientY : event.touches[0].clientY;

            drag.current = event.target;
            drag.current.classList.add('moving');
            drag.current.style.setProperty('z-index', String(++drags.current));

            dragData.current = {
                row,
                cell,
                position: {x: clientX, y: clientY}
            };
        }
    };

    const handleMouseMove = ({ nativeEvent: event }: React.MouseEvent<EventTarget> | React.TouchEvent<EventTarget>): void => {
        if (movement === 'select' || ! (event.target instanceof HTMLDivElement) || ! event.target.classList.contains('setting-cell-outer')) {
            return;
        }

        if (! drag.current || dragData.current === null) {
            return;
        }

        const clientX = event instanceof MouseEvent ? event.clientX : event.touches[0].clientX;
        const clientY = event instanceof MouseEvent ? event.clientY : event.touches[0].clientY;

        drag.current.style.setProperty('left', `${clientX - dragData.current.position.x}px`);
        drag.current.style.setProperty('top', `${clientY - dragData.current.position.y}px`);
    }

    const handleMouseUp = ({ nativeEvent: event }: React.MouseEvent<EventTarget> | React.TouchEvent<EventTarget>): void => {
        if (! (event.target instanceof HTMLDivElement)) {
            return;
        }

        if (movement === 'select' && event.target.classList.contains('setting-cell-outer')) {
            if (firstSelection.current && secondSelection.current) {
                if (firstSelection.current !== event.target && secondSelection.current !== event.target) {
                    swapSelectedCells();
                }

                firstSelection.current.classList.remove('selected');
                secondSelection.current.classList.remove('selected');

                firstSelection.current = null;
                secondSelection.current = null;
            } else if (! firstSelection.current) {
                firstSelection.current = event.target;
                firstSelection.current.classList.add('selected');
            } else if (firstSelection.current && firstSelection.current === event.target) {
                firstSelection.current.classList.remove('selected');
                firstSelection.current = null;
            } else if (firstSelection.current) {
                secondSelection.current = event.target;
                secondSelection.current.classList.add('selected');

                swapSelectedCells();
            }

            event.preventDefault();

            return;
        }

        if (movement === 'select' || ! drag.current || dragData.current === null) {
            return;
        }

        event.preventDefault();

        const clientX = event instanceof MouseEvent ? event.clientX : event.changedTouches[0].clientX;
        const clientY = event instanceof MouseEvent ? event.clientY : event.changedTouches[0].clientY;
        const boardElements = [...document.querySelectorAll('#settings .setting-cell-outer')];
        const target = boardElements.find(element => {
            if (element === drag.current) {
                return false;
            }

            const bcr = element.getBoundingClientRect();

            if (clientX >= bcr.x && clientX <= bcr.x + bcr.width && clientY >= bcr.y && clientY <= bcr.y + bcr.height) {
                return true;
            }

            return false;
        });

        if (target instanceof HTMLDivElement && target.classList.contains('setting-cell-outer')) {
            const index = boardElements.indexOf(target);
            const row = Math.floor(index / 2);
            const cell = index % 2;
            const targetRow = dragData.current.row;
            const targetCell = dragData.current.cell;
            const updatedBoard = [...board];

            [updatedBoard[row][cell], updatedBoard[targetRow][targetCell]] = [board[targetRow][targetCell], board[row][cell]];

            setBoard(updatedBoard);
        }

        drag.current.classList.remove('moving');
        drag.current.style.setProperty('left', '0px');
        drag.current.style.setProperty('top', '0px');
        drag.current = null;
        dragData.current = null;
    }

    const daily = JSON.parse(atob(localStorage.getItem('daily') || 'e30='));
    const results = JSON.parse(localStorage.getItem('results') || '[]');
    let dailyComplete = daily.hasOwnProperty('id') ? false : true;

    if (results.find((result: Record<string, number>) => result.p === daily.id)) {
        dailyComplete = true;
    }

    return (
        <>
            {modal === true && setShowSettings && <div id="settings-background" onClick={() => setShowSettings(false)} className={visible ? 'active' : 'hidden'}></div>}
            <div id="settings" 
                className={visible ? 'modal active' : modal === false ? 'active' : 'hidden'}
                onMouseMove={(event: React.MouseEvent<HTMLElement>) => handleMouseMove(event)}
                onMouseUp={(event: React.MouseEvent<HTMLElement>) => handleMouseUp(event)} 
                onTouchMove={(event: React.TouchEvent<HTMLElement>) => handleMouseMove(event)}
                onTouchEnd={(event: React.TouchEvent<HTMLElement>) => handleMouseUp(event)}>
                <h4>Settings</h4>
                <div className="setting">
                    <h5>Mosaic Size</h5>
                    {dailyComplete && <>
                        <div id="settings-toggles">
                            <div className={`settings-toggle${size === 4 ? ' active' : ''}`} onClick={() => setSize(4)}>4 x 4</div>
                            <div className={`settings-toggle${size === 5 ? ' active' : ''}`} onClick={() => setSize(5)}>5 x 5</div>
                        </div>
                        <div className="setting-description">
                            {size === 4 && 'Solve a 4 x 4 puzzle within 15 moves. All puzzles can be solved in a minimum of 10 moves. Earn a star for each move you have remaining.'}
                            {size === 5 && 'Solve a 5 x 5 puzzle within 25 moves. All puzzles can be solved in a minimum of 20 moves. Earn a star for each move you have remaining.'}
                        </div>
                    </>}
                    {!dailyComplete && <>
                        <div id="settings-toggles">
                            <div className={`settings-toggle locked${size === 4 ? ' active' : ''}`}>4 x 4</div>
                            <div className={`settings-toggle locked${size === 5 ? ' active' : ''}`}>5 x 5</div>
                        </div>
                        <div className="setting-description">
                            You cannot change the size while you have a Daily Mosaic still in progress.
                        </div>
                    </>}
                </div>
                <div className="setting">
                    <h5>Tile Movement</h5>
                    <div id="settings-toggles">
                        <div className={`settings-toggle${movement === 'drag' ? ' active' : ''}`} onClick={() => setMovement('drag')}>Drag</div>
                        <div className={`settings-toggle${movement === 'select' ? ' active' : ''}`} onClick={() => setMovement('select')}>Select</div>
                    </div>
                    <div className="setting-example">
                    {board.map((row, rowKey) => (
                        <Fragment key={rowKey}>
                            {row.map((cell, cellKey) => (
                            <div 
                                key={cellKey} 
                                className="setting-cell-outer" 
                                onMouseDown={(event: React.MouseEvent<HTMLDivElement>) => handleMouseDown(event, rowKey, cellKey)}
                                onTouchStart={(event: React.TouchEvent<HTMLDivElement>) => handleMouseDown(event, rowKey, cellKey)}>
                                <div className="cell-front">{cell}</div>
                            </div>
                            ))}
                        </Fragment>
                    ))}
                    </div>
                    <div className="setting-description">
                        {movement === 'select' && 'To swap two tiles, select the two you would like to swap. If you are happy with their new position, select one of the same two again to confirm the swap. To cancel the swap, select any other tile.'}
                        {movement === 'drag' && 'To swap two tiles, select the first one you would like to move and drag it over the top of the second you would like to swap it with. To confirm the swap, simply release the selected tile.'}
                    </div>
                </div>
                <button className="button" onClick={() => confirmSettings()}>Confirm</button>
                {setShowSettings && <div id="close-menu" onClick={() => setShowSettings(false)}>x</div>}
            </div>
        </>
    );
};

export default Settings;